///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { AssignmentMethodEnum, AutoDesignP2MPDTO } from '../../../generated/shared-types.model';
import { GtthLogicalTopology } from '../../../project-view-panel/logical-topology/entities/logical-topology';
import { isDefined } from '@siklu/sui-lib/utils';

export class AutoDesignPtMpModel implements AutoDesignP2MPDTO {
    public static readonly TUNNEL_FAC_DEFAULT_VALUE_FOR_MH_TG = 5.0;
    public static readonly TUNNEL_FAC_DEFAULT_VALUE_FOR_NON_MH_TG = 3.0;
    public static readonly TUNNEL_FAC_DEFAULT_VALUE_FOR_NO_RESILIENCY = 1.0;
    public static readonly TUNNEL_FAC_MIN_VALUE_FOR_NO_RESILIENCY = 0;

    public static readonly REDUND_FAC_FOR_RESILIENCY = 1.0;
    public static readonly REDUND_FAC_FOR_NO_RESILIENCY = 0;

    as_method: AssignmentMethodEnum;
    bottom_up: boolean;
    feed_by: number;
    layer_idx: number;
    max_dev_num: number;
    num_dev_fac: number;
    opt_bs_fac: number;
    p2p_dist_fac: number;
    pole_block: number;
    pop_dist_fac: number;
    pref_forced: boolean;
    rand_fac: number;
    rand_seed: number;
    rev_feed_fac: number;
    buDeviceAzimuth: number;
    //        for more details see https://jira-siklu.atlassian.net/browse/PLANNER-2181
    readonly cap_fac = 1.0;
    cost_fac: number;
    lcent_dist: number;
    lim_boxes: boolean;
    n_cand_win: number;
    peer_bs_fac: number;
    redun_fac: number;
    tg_max_ittr: number;
    tunnel_fac: number;
    take_rate: number;

    constructor(data: any) {
        if (data) {
            this.as_method = data.as_method;
            this.bottom_up = data.bottom_up;
            this.feed_by = data.feed_by;
            this.layer_idx = data.layer_idx;
            this.max_dev_num = data.max_dev_num;
            this.num_dev_fac = data.num_dev_fac;
            this.opt_bs_fac = data.opt_bs_fac;
            this.p2p_dist_fac = data.p2p_dist_fac;
            this.pole_block = data.pole_block;
            this.pop_dist_fac = data.pop_dist_fac;
            this.pref_forced = data.pref_forced;
            this.rand_fac = data.rand_fac;
            this.rand_seed = data.rand_seed;
            this.rev_feed_fac = data.rev_feed_fac;
            this.buDeviceAzimuth = data.buDeviceAzimuth;
            this.cost_fac = data.cost_fac;
            this.lcent_dist = data.lcent_dist;
            this.lim_boxes = data.lim_boxes;
            this.n_cand_win = data.n_cand_win;
            this.peer_bs_fac = data.peer_bs_fac;
            this.redun_fac = data.redun_fac;
            this.tg_max_ittr = data.tg_max_ittr;
            this.tunnel_fac = data.tunnel_fac;
            this.take_rate = data.take_rate;
        }
    }

    static createAutoDesignPtMpModel(data: AutoDesignPtMpModel, layerContainsMhTg: boolean): AutoDesignPtMpModel {
        const ptmpDesignDTO = new AutoDesignPtMpModel({
            as_method: AutoDesignPtMpModel.setValueOrDefault(data?.as_method, AssignmentMethodEnum.EQUALIZE),
            bottom_up: AutoDesignPtMpModel.setValueOrDefault(data?.bottom_up, true),
            feed_by: AutoDesignPtMpModel.setValueOrDefault(data?.feed_by, GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX),
            max_dev_num: AutoDesignPtMpModel.setValueOrDefault(data?.max_dev_num, 2),
            num_dev_fac: AutoDesignPtMpModel.setValueOrDefault(data?.num_dev_fac, 2),
            opt_bs_fac: AutoDesignPtMpModel.setValueOrDefault(data?.opt_bs_fac, 1),
            p2p_dist_fac: AutoDesignPtMpModel.setValueOrDefault(data?.p2p_dist_fac, layerContainsMhTg ? 1.0 : 0.2),
            pole_block: AutoDesignPtMpModel.setValueOrDefault(data?.pole_block, 40),
            pop_dist_fac: AutoDesignPtMpModel.setValueOrDefault(data?.pop_dist_fac, 1.0),
            pref_forced: AutoDesignPtMpModel.setValueOrDefault(data?.pref_forced, false),
            rand_fac: AutoDesignPtMpModel.setValueOrDefault(data?.rand_fac, 3),
            rand_seed: AutoDesignPtMpModel.setValueOrDefault(data?.rand_seed, 1),
            rev_feed_fac: AutoDesignPtMpModel.setValueOrDefault(data?.rev_feed_fac, -1.0),
            buDeviceAzimuth: AutoDesignPtMpModel.setValueOrDefault(data?.buDeviceAzimuth, 0),
            cost_fac: AutoDesignPtMpModel.setValueOrDefault(data?.cost_fac, layerContainsMhTg ? 0 : 1.5),
            lcent_dist: AutoDesignPtMpModel.setValueOrDefault(data?.lcent_dist, 45),
            lim_boxes: AutoDesignPtMpModel.setValueOrDefault(data?.lim_boxes, false),
            n_cand_win: AutoDesignPtMpModel.setValueOrDefault(data?.n_cand_win, 2),
            peer_bs_fac: AutoDesignPtMpModel.setValueOrDefault(data?.peer_bs_fac, 0.3),
            redun_fac: AutoDesignPtMpModel.setValueOrDefault(data?.redun_fac, AutoDesignPtMpModel.REDUND_FAC_FOR_NO_RESILIENCY),
            tg_max_ittr: AutoDesignPtMpModel.setValueOrDefault(data?.tg_max_ittr, 10000),
            tunnel_fac: AutoDesignPtMpModel.setValueOrDefault(
                data?.tunnel_fac,
                AutoDesignPtMpModel.TUNNEL_FAC_DEFAULT_VALUE_FOR_NO_RESILIENCY
            ),
            take_rate: AutoDesignPtMpModel.setValueOrDefault(data?.take_rate, 100)
        });

        if (ptmpDesignDTO.isResiliencyEnabled()) {
            ptmpDesignDTO.peer_bs_fac = ptmpDesignDTO.peer_bs_fac > 0 ? ptmpDesignDTO.peer_bs_fac : 0.3;
        } else {
            ptmpDesignDTO.peer_bs_fac = 0;
        }

        return ptmpDesignDTO;
    }

    static setValueOrDefault(value: any, defaultValue: any): any {
        return isDefined(value) ? value : defaultValue;
    }

    public triggerReverseFeedCheck(): void {
        this.rev_feed_fac = this.rev_feed_fac === -1 ? 1 : -1;
    }

    public isResiliencyEnabled(): boolean {
        return this.redun_fac > AutoDesignPtMpModel.REDUND_FAC_FOR_NO_RESILIENCY;
    }
}
